import { makeAutoObservable, runInAction } from 'mobx';
import { BACKEND_MODULE_TYPES, EXCHANGE_AMOUNT_DIRECTION } from 'components/common/constants';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import {
  createExchangeQuoteRequest,
  calculateExchangeDataRequest,
  createExchangeTransferRequest,
  confirmExchangeTransferRequest,
  resendSecurityCode,
  forceChangeConfirmationTypeRequest
} from 'services/requestAgent';
import { amountFormattedValue, isCryptoProvider } from 'services/utils';

class CurrencyExchangeStore {
  isRepeatTransaction = false;
  isRedirectFromPaymentForm = false;
  isExchangeLoading = false;
  isCalculatedFromToValueAndCommission = false;
  isExchangeTransferCreated = false;
  isExchangeSucceeded = false;
  isAmountExceeded = false;
  isShowSwitchConfirmationMethodScreen = false;
  error = null;
  confirmationPopupError = null;
  accountFrom = null;
  accountTo = null;
  amountFrom = null;
  amountTo = null;
  exchangeData = {
    quoteId: null,
    rate: null,
    commission: null,
    expiresAt: null,
    duration: null
  };
  createdExchangeTransferData = {
    confirmationId: null,
    rate: null,
    commission: null,
    accountFrom: null,
    accountTo: null,
    amountFrom: null,
    amountTo: null
  };
  fieldsAreDisabled = false;

  constructor() {
    makeAutoObservable(this);
  }

  resetExchangeStore() {
    this.isRepeatTransaction = false;
    this.isRedirectFromPaymentForm = false;
    this.isExchangeLoading = false;
    this.isCalculatedFromToValueAndCommission = false;
    this.isExchangeTransferCreated = false;
    this.isExchangeSucceeded = false;
    this.isAmountExceeded = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.error = null;
    this.confirmationPopupError = null;
    this.accountFrom = null;
    this.accountTo = null;
    this.amountFrom = null;
    this.amountTo = null;
    this.exchangeData = {
      quoteId: null,
      rate: null,
      commission: null,
      expiresAt: null,
      duration: null
    };
    this.createdExchangeTransferData = {
      confirmationId: null,
      rate: null,
      commission: null,
      accountFrom: null,
      accountTo: null,
      amountFrom: null,
      amountTo: null
    };
  }

  setIsRepeatTransactionStatus(status) {
    this.isRepeatTransaction = status;
  }

  setIsRedirectFromPaymentFormStatus(status) {
    this.isRedirectFromPaymentForm = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setFromToAccounts(from, to) {
    this.accountFrom = from;
    this.accountTo = to;
    this.error = null;
  }

  setOppositeAmountToZero(amountDirection) {
    this[amountDirection] = '0';
    this.isCalculatedFromToValueAndCommission = false;
    this.exchangeData = {
      ...this.exchangeData,
      commission: null
    };
  }

  setAmount(amountType, value) {
    this[amountType] = value;

    if (Number(value) === 0) {
      if (amountType === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM) {
        this.setOppositeAmountToZero(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_TO);
      } else {
        this.setOppositeAmountToZero(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM);
      }
    }
  }

  setIsExchangeLoading(status) {
    this.isExchangeLoading = status;
    this.error = null;
    this.fieldsAreDisabled = false;
  }

  resetAfterSuccessfulExchange() {
    this.confirmationPopupError = null;
    this.amountFrom = null;
    this.amountTo = null;
    this.isExchangeSucceeded = false;
    this.isAmountExceeded = false;
    this.createdExchangeTransferData = {
      confirmationId: null,
      rate: null,
      commission: null,
      accountFrom: null,
      accountTo: null,
      amountFrom: null,
      amountTo: null
    };
  }

  resetCreatedExchangeTransfer() {
    this.isExchangeTransferCreated = false;
    this.isAmountExceeded = false;
    this.createdExchangeTransferData = {
      confirmationId: null,
      rate: null,
      commission: null,
      accountFrom: null,
      accountTo: null,
      amountFrom: null,
      amountTo: null
    };
  }

  clearConfirmationPopupError() {
    this.confirmationPopupError = null;
  }

  async createExchangeQuote(customerNumber) {
    try {
      this.setIsExchangeLoading(true);

      const {
        quote_id: quoteId,
        rate,
        expiresAt,
        duration
      } = await createExchangeQuoteRequest(customerNumber, {
        wallet_from: this.accountFrom?.wallet_number,
        wallet_to: this.accountTo?.wallet_number
      });

      runInAction(() => {
        this.exchangeData = {
          ...this.exchangeData,
          quoteId,
          duration,
          rate,
          expiresAt
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        if (
          err.code === 'EXCHANGE_PAIR_DISABLED' ||
          err.code === 'EXCHANGE_RATE_NOT_FOUND' ||
          err.code === 'PAYMENT_CREATION_TIME_EXPIRED' ||
          (err?.code === 'NOT_FOUND' && err?.message?.includes('Exchange rate pair'))
        ) {
          this.fieldsAreDisabled = true;
        }
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async calculateExchangeData(customerNumber, amount, direction) {
    try {
      this.setIsExchangeLoading(true);

      const {
        from: amountFrom,
        to: amountTo,
        commission,
        amountExceeded
      } = await calculateExchangeDataRequest(customerNumber, {
        quote_id: this.exchangeData.quoteId,
        amount,
        direction: direction === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM ? 'AMOUNT_FROM' : 'AMOUNT_TO'
      });

      runInAction(() => {
        if (direction === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM) {
          this.amountTo = amountFormattedValue(
            amountTo,
            isCryptoProvider(this.accountTo?.transfer_provider) ? CRYPTO_CURRENCY_PRECISION : 2,
            true
          );
        } else {
          this.amountFrom = amountFormattedValue(
            amountFrom,
            isCryptoProvider(this.accountFrom?.transfer_provider) ? CRYPTO_CURRENCY_PRECISION : 2,
            true
          );
        }
        this.exchangeData = {
          ...this.exchangeData,
          commission
        };
        this.isAmountExceeded = amountExceeded;
        this.isCalculatedFromToValueAndCommission = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isCalculatedFromToValueAndCommission = false;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async createExchangeTransfer(customerNumber) {
    try {
      this.setIsExchangeLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await createExchangeTransferRequest(customerNumber, {
        quote_id: this.exchangeData.quoteId,
        amount: this.amountFrom
      });

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.createdExchangeTransferData = {
          confirmationId: confirmationId,
          rate: this.exchangeData.rate,
          commission: this.exchangeData.commission,
          accountFrom: this.accountFrom,
          accountTo: this.accountTo,
          amountFrom: this.amountFrom,
          amountTo: this.amountTo
        };
        this.isExchangeTransferCreated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isExchangeTransferCreated = false;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async confirmExchangeTransfer(customerNumber, code) {
    try {
      this.setIsExchangeLoading(true);

      await confirmExchangeTransferRequest(customerNumber, {
        confirmation_id: this.createdExchangeTransferData.confirmationId,
        code
      });

      runInAction(() => {
        this.isExchangeTransferCreated = false;
        this.isExchangeSucceeded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
        this.isExchangeSucceeded = false;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async resendExchangeSecurityCode() {
    try {
      this.setIsExchangeLoading(false);
      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.PAYMENT, this.createdExchangeTransferData.confirmationId);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.createdExchangeTransferData.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(
        this.createdExchangeTransferData.confirmationId,
        confirmationType,
        BACKEND_MODULE_TYPES.PAYMENT
      );

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.createdExchangeTransferData.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };
}

export default new CurrencyExchangeStore();
